import { useState } from 'react';

import { useGitCredentials } from '../../hooks/use-git-credentials';
import type { GitCredentialResponse, GitCredentialTestResponse } from '../../lib/api-client';

import { GitCredentialForm } from './GitCredentialForm';

/**
 * The tokens the platform clones WITH.
 *
 * Its own section rather than a Proxy Vault connection, because a clone is a
 * server-side fetch that never traverses the egress proxy — there is no route
 * to match and no stand-in to stamp. And its own section rather than a free-text
 * box inside the Organization view, which is what it replaced: that box asked an
 * operator to type a raw credential id they had to go and find somewhere else.
 *
 * No token is ever rendered. A bound credential proves itself with a
 * fingerprint, and the only way to learn whether it WORKS is to press Test,
 * which asks the real remote.
 */

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const ghostBtn: React.CSSProperties = {
  padding: '4px 9px',
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const primaryBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
};

/** Colour a status the way an operator reads it, not the way it is stored. */
function StatusPill({ status }: { status: GitCredentialResponse['status'] }) {
  const tone =
    status === 'active'
      ? { fg: 'var(--kind-success, #16a34a)', label: 'active' }
      : status === 'revoked'
        ? { fg: 'var(--kind-failure)', label: 'revoked' }
        : { fg: 'var(--kind-warn, #d97706)', label: 'needs re-entry' };
  return (
    <span data-testid="gc-status" style={{ fontSize: 10, color: tone.fg, fontWeight: 500 }}>
      ● {tone.label}
    </span>
  );
}

function TestResult({ result }: { result: GitCredentialTestResponse }) {
  // Say what to DO about it. "auth_failed" alone sends an operator to check the
  // URL, which is the one thing that is definitely fine.
  const advice: Record<string, string> = {
    ok: 'the remote accepted this credential',
    auth_failed: 'the remote rejected this token — rotate it',
    not_found: 'authenticated, but that repository is not visible to this token',
    no_material: 'no token stored — supply one with Rotate',
    host_not_allowed: 'this credential is restricted to another host',
    revoked: 'this credential is revoked',
    unreachable: 'could not reach the host',
    timeout: 'the host did not answer',
    bad_url: 'that is not an https remote',
  };
  return (
    <div
      data-testid="gc-test-result"
      style={{
        fontSize: 11,
        marginTop: 6,
        padding: '6px 8px',
        borderRadius: 6,
        background: 'var(--surface-2, var(--surface))',
        border: `1px solid ${result.reachable ? 'var(--border)' : 'var(--kind-failure)'}`,
        color: result.reachable ? 'var(--text-soft)' : 'var(--kind-failure)',
      }}
    >
      <strong>{result.reachable ? '✓' : '✗'} {result.result}</strong> — {advice[result.result] ?? 'unrecognised outcome'}
      <div style={{ color: 'var(--text-mute)', marginTop: 2, fontFamily: mono, fontSize: 10 }}>
        {result.repo_url}
        {result.detail ? ` · ${result.detail}` : ''}
      </div>
    </div>
  );
}

export function GitCredentialsView() {
  const {
    credentials,
    status,
    error,
    createCredential,
    updateCredential,
    deleteCredential,
    testCredential,
  } = useGitCredentials();
  const [adding, setAdding] = useState(false);
  const [rotating, setRotating] = useState<GitCredentialResponse | null>(null);
  const [tests, setTests] = useState<Record<string, GitCredentialTestResponse>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [busyErr, setBusyErr] = useState<string | undefined>();

  async function guard(fn: () => Promise<unknown>) {
    setBusyErr(undefined);
    try {
      await fn();
    } catch (err) {
      setBusyErr(err instanceof Error ? err.message : 'Failed');
    }
  }

  async function runTest(c: GitCredentialResponse) {
    setTesting(c.id);
    setBusyErr(undefined);
    try {
      // Untested credentials need a remote to aim at; attached ones already
      // have one, and the server picks it so the operator need not remember.
      const repoUrl = c.in_use_by.length
        ? undefined
        : (window.prompt(`Test "${c.name}" against which repository URL?`) ?? undefined);
      if (!c.in_use_by.length && !repoUrl) return;
      const result = await testCredential(c.id, repoUrl);
      setTests((prev) => ({ ...prev, [c.id]: result }));
    } catch (err) {
      setBusyErr(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(null);
    }
  }

  return (
    <div
      data-testid="git-credentials-view"
      style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Git Credentials</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {credentials.length} stored
        </div>
        {!adding && (
          <button
            type="button"
            data-testid="gc-add"
            style={{ ...primaryBtn, marginLeft: 'auto' }}
            onClick={() => setAdding(true)}
          >
            + Add credential
          </button>
        )}
      </div>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--text-mute)', maxWidth: 720, lineHeight: 1.5 }}>
          These authenticate <strong>clones</strong> — a server-side fetch the platform makes on
          your behalf. They are separate from the Proxy Vault, which substitutes credentials on
          traffic leaving a sandbox. A repository with no credential clones with the platform
          token.
        </div>

        {status === 'error' && (
          <div style={{ fontSize: 12, color: 'var(--kind-failure)' }}>{error}</div>
        )}
        {busyErr && <div style={{ fontSize: 12, color: 'var(--kind-failure)' }}>{busyErr}</div>}

        {adding && (
          <GitCredentialForm
            mode="create"
            onCancel={() => setAdding(false)}
            onSubmit={async (body) => {
              await guard(async () => {
                await createCredential(body as never);
                setAdding(false);
              });
            }}
          />
        )}

        {status === 'success' && credentials.length === 0 && !adding && (
          <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>
            No git credentials yet. Repositories clone with the platform token until one is added.
          </div>
        )}

        {credentials.map((c) => (
          <div
            key={c.id}
            data-testid="gc-row"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              background: 'var(--surface)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>{c.provider}</span>
              {c.host_pattern && (
                <span style={{ fontSize: 10, color: 'var(--text-soft)', fontFamily: mono }}>
                  {c.host_pattern}
                </span>
              )}
              <span style={{ marginLeft: 'auto' }}>
                <StatusPill status={c.status} />
              </span>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text-soft)', fontFamily: mono }}>
              {/* The fingerprint is the whole disclosure: enough to answer "did
                  the rotation take?", useless to anyone who steals the page. */}
              {c.fingerprint ? `fingerprint ${c.fingerprint}` : 'no material stored'} · as{' '}
              {c.username}
              {c.expires_at ? ` · expires ${fmtDate(c.expires_at)}` : ''}
              {c.last_used_at ? ` · last used ${fmtDate(c.last_used_at)}` : ' · never used'}
            </div>

            <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>
              {c.in_use_by.length
                ? `used by ${c.in_use_by.length} ${c.in_use_by.length === 1 ? 'repository' : 'repositories'}`
                : 'not attached to a repository'}
            </div>

            {c.status === 'needs_reentry' && (
              <div style={{ fontSize: 11, color: 'var(--kind-warn, #d97706)' }}>
                Carried across from the retired credential store, which kept this value in a form
                that could not be moved. Rotate it to supply the token again.
              </div>
            )}

            {tests[c.id] && <TestResult result={tests[c.id]!} />}

            <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="gc-test"
                style={ghostBtn}
                disabled={testing === c.id}
                onClick={() => void runTest(c)}
              >
                {testing === c.id ? 'Testing…' : 'Test'}
              </button>
              <button
                type="button"
                data-testid="gc-rotate"
                style={ghostBtn}
                onClick={() => setRotating(c)}
              >
                Rotate
              </button>
              <button
                type="button"
                data-testid="gc-toggle"
                style={ghostBtn}
                onClick={() =>
                  guard(() =>
                    updateCredential(c.id, {
                      status: c.status === 'revoked' ? 'active' : 'revoked',
                    }),
                  )
                }
              >
                {c.status === 'revoked' ? 'Restore' : 'Revoke'}
              </button>
              <button
                type="button"
                data-testid="gc-delete"
                style={{ ...ghostBtn, color: 'var(--kind-failure)' }}
                disabled={c.in_use_by.length > 0}
                title={
                  c.in_use_by.length
                    ? 'Detach it from every repository first'
                    : 'Delete this credential'
                }
                onClick={() => guard(() => deleteCredential(c.id))}
              >
                Delete
              </button>
            </div>

            {rotating?.id === c.id && (
              <GitCredentialForm
                mode="rotate"
                initial={c}
                onCancel={() => setRotating(null)}
                onSubmit={async (body) => {
                  await guard(async () => {
                    await updateCredential(c.id, body);
                    setRotating(null);
                    // A rotation invalidates whatever the last probe proved.
                    setTests((prev) => {
                      const next = { ...prev };
                      delete next[c.id];
                      return next;
                    });
                  });
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
